import api from '../api/axios';

/**
 * Client for System Admin → Errors (tdp-api routes/systemAdminErrors.ts,
 * docs/error-diagnostics-plan.md §6). Errors are recorded server-side into the
 * `ops` schema as GROUPS (one per distinct failure: event + where + error
 * code) holding OCCURRENCES (each time it happened). Everything here is ids,
 * error codes and already-scrubbed log lines — no child or staff data.
 */

export type ErrorStatus = 'new' | 'investigating' | 'resolved' | 'ignored';

export const ERROR_STATUSES: ErrorStatus[] = ['new', 'investigating', 'resolved', 'ignored'];

export const ERROR_STATUS_LABELS: Record<ErrorStatus, string> = {
  new: 'New',
  investigating: 'Investigating',
  resolved: 'Resolved',
  ignored: 'Ignored',
};

export type ErrorPeriod = '24h' | '7d' | '30d' | '365d';

export const ERROR_PERIOD_OPTIONS: { id: ErrorPeriod; label: string }[] = [
  { id: '24h', label: '24 hours' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '365d', label: '1 year' },
];

export interface ErrorGroup {
  id: number;
  /** The log event, e.g. "posts.create_failed". */
  event: string;
  /** API route pattern ("POST /api/posts") — null for background jobs. */
  route: string | null;
  /** Background job name (pdfWorker, mediaSweep…) — null for API requests. */
  job: string | null;
  errName: string | null;
  /** Postgres SQLSTATE, Node code, AWS error name… */
  errCode: string | null;
  errConstraint: string | null;
  /** The latest occurrence's (scrubbed) message. */
  sampleMessage: string | null;
  status: ErrorStatus;
  /** All time. */
  occurrenceCount: number;
  /** List only: occurrences within the selected period. */
  periodOccurrences?: number;
  /** List only: distinct organisations affected within the period. */
  periodOrganisations?: number;
  firstSeenAt: string;
  lastSeenAt: string;
  /** Times it came back after being resolved. */
  reopenedCount: number;
  resolvedAt: string | null;
  /** Email of the system admin who resolved it. */
  resolvedBy: string | null;
  notes: string | null;
}

export interface ErrorOccurrence {
  id: number;
  groupId: number;
  occurredAt: string;
  /** The reference a user reads out (X-Request-Id). */
  requestId: string | null;
  apigwRequestId: string | null;
  /** For worker errors: the API request that invoked the worker. */
  parentRequestId: string | null;
  organisationId: number | null;
  userId: number | null;
  activeUserId: number | null;
  method: string | null;
  path: string | null;
  job: string | null;
  /** CloudWatch Logs Insights, pre-filtered to this request. null when not configured. */
  logsLink: string | null;
  /** Occurrence page only: the stored (scrubbed) log line. */
  record?: Record<string, any>;
}

export interface ErrorGroupList {
  groups: ErrorGroup[];
  total: number;
  limit: number;
  offset: number;
}

export interface ErrorGroupFilters {
  /** One or more statuses; empty = all. */
  statuses: ErrorStatus[];
  period: ErrorPeriod;
  orgId?: number | null;
  limit?: number;
  offset?: number;
}

export class SystemAdminErrorsService {
  /** Groups last seen within the period, most recent first. */
  static async fetchGroups(f: ErrorGroupFilters): Promise<ErrorGroupList> {
    const response = await api.get('/system-admin/errors', {
      params: {
        status: f.statuses.length ? f.statuses.join(',') : 'all',
        period: f.period,
        orgId: f.orgId ?? undefined,
        limit: f.limit ?? 50,
        offset: f.offset ?? 0,
      },
    });
    return response.data;
  }

  /** One group plus its latest occurrences (newest first). */
  static async fetchGroup(groupId: number, limit = 50, offset = 0): Promise<{ group: ErrorGroup; occurrences: ErrorOccurrence[] }> {
    const response = await api.get(`/system-admin/errors/${groupId}`, { params: { limit, offset } });
    return response.data;
  }

  /** Triage. `notes: ''` clears them. Resolving records who did it. */
  static async updateGroup(groupId: number, change: { status?: ErrorStatus; notes?: string }): Promise<ErrorGroup> {
    const response = await api.patch(`/system-admin/errors/${groupId}`, change);
    return response.data;
  }

  /** One occurrence with its stored log line. 404 after the 30-day retention. */
  static async fetchOccurrence(occurrenceId: number): Promise<ErrorOccurrence> {
    const response = await api.get(`/system-admin/errors/occurrences/${occurrenceId}`);
    return response.data;
  }

  /**
   * Find by the reference shown to a user ("Ref: …"). Matches the request
   * itself and any worker (PDF, embedding, email) it invoked.
   */
  static async findByReference(reference: string): Promise<ErrorOccurrence[]> {
    const response = await api.get(`/system-admin/errors/by-request/${encodeURIComponent(reference)}`);
    return response.data.occurrences || [];
  }
}

/** "POST /api/posts" for a request, "pdfWorker" for a job. */
export function errorWhere(g: { route?: string | null; job?: string | null; method?: string | null; path?: string | null }): string {
  if (g.route) return g.route;
  if (g.method && g.path) return `${g.method} ${g.path}`;
  return g.job || '—';
}

/** Pull a reference out of pasted text: "Ref: 3f2a…", a full UUID, or the bare id. */
export function parseReference(text: string): string | null {
  const t = text.trim();
  const uuid = t.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  if (uuid) return uuid[0].toLowerCase();
  const bare = t.replace(/^ref:?\s*/i, '').replace(/[()\s.]+$/g, '');
  return /^[A-Za-z0-9-]{1,100}$/.test(bare) ? bare : null;
}
